import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, Snackbar, TextField,
} from '@mui/material';
import { registrarEmailsProcesso } from '../../../../services/core/processos/processosService';
import { extractResponseErrorMessage, isValidationException } from '../../../../helpers/utils/form.utils';
import useSessaoDialogMutacao from '../../../hooks/useSessaoDialogMutacao';

const MENSAGEM_ERRO_GENERICA = 'Não foi possível registrar o e-mail.';
const QTD_PALAVRAS_MINIMAS = 5;
// Mesmo único campo de `dialogEmailController.makeErrorFields` real.
const CAMPOS_FORMULARIO = ['email'];

function contarPalavras(texto) {
  if (!texto || !texto.trim()) {
    return 0;
  }
  return texto.trim().split(/\s+/).length;
}

/**
 * Port de `DialogEmail.jsx`/`dialogEmailController.js` (tela real,
 * `EmailsProcesso/DialogEmail/`) para MUI v5 — AW2-1092 Etapa 1. Único
 * campo de texto livre, mínimo 5 palavras (mesmo critério real via
 * `contarPalavras`) — sem Tipo Destinatário/Para/Cópia/Assunto (diferente
 * do dialog de e-mail de documentos, AW2-1094 Etapa 6: aquele é um disparo
 * com múltiplos campos, este é um simples registro de texto no histórico).
 *
 * Guarda de sessão/duplo-clique compartilhada — ver
 * `src/prototipo/hooks/useSessaoDialogMutacao.js`.
 */
export default function DialogNovoEmail({
  open, idProcesso, onClose, onSucesso,
}) {
  const [texto, setTexto] = useState('');
  const [erroCampo, setErroCampo] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState('');

  const { sessaoRef, enviandoRef } = useSessaoDialogMutacao(open, idProcesso, () => {
    setTexto('');
    setErroCampo('');
    setErro('');
    setEnviando(false);
  });

  const onChangeTexto = (event) => {
    setTexto(event.target.value);
    setErroCampo('');
  };

  const totalPalavras = contarPalavras(texto);
  const isConfirmarDesabilitado = totalPalavras < QTD_PALAVRAS_MINIMAS || enviando;

  const onSubmit = async () => {
    if (enviandoRef.current || totalPalavras < QTD_PALAVRAS_MINIMAS) {
      return;
    }

    const sessaoAlvo = sessaoRef.current;
    enviandoRef.current = true;
    setEnviando(true);
    setErroCampo('');
    setErro('');

    try {
      await registrarEmailsProcesso(idProcesso, texto);
      if (sessaoRef.current !== sessaoAlvo) {
        return;
      }
      onSucesso();
      onClose();
    } catch (exception) {
      if (sessaoRef.current !== sessaoAlvo) {
        return;
      }
      if (isValidationException(exception)) {
        const validations = exception.response.data.validations || [];
        const erroEmail = validations.find(v => CAMPOS_FORMULARIO.includes(v.field));
        if (erroEmail) {
          setErroCampo(erroEmail.message);
        } else {
          setErro(MENSAGEM_ERRO_GENERICA);
        }
      } else {
        setErro(extractResponseErrorMessage(exception) || MENSAGEM_ERRO_GENERICA);
      }
    } finally {
      if (sessaoRef.current === sessaoAlvo) {
        enviandoRef.current = false;
        setEnviando(false);
      }
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>Novo E-mail</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            minRows={6}
            label="E-mail*"
            placeholder="Inserir texto do e-mail"
            value={texto}
            onChange={onChangeTexto}
            error={Boolean(erroCampo)}
            helperText={erroCampo}
            size="small"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={enviando}>Cancelar</Button>
          <Button onClick={onSubmit} variant="contained" disabled={isConfirmarDesabilitado}>
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={Boolean(erro)}
        autoHideDuration={4000}
        onClose={() => setErro('')}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="error" variant="filled" onClose={() => setErro('')}>
          {erro}
        </Alert>
      </Snackbar>
    </>
  );
}

DialogNovoEmail.propTypes = {
  open: PropTypes.bool,
  idProcesso: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onClose: PropTypes.func.isRequired,
  onSucesso: PropTypes.func.isRequired,
};

DialogNovoEmail.defaultProps = {
  open: false,
  idProcesso: null,
};
